import { cache } from 'react';
import { z } from 'zod';

import { apiRevalidateSeconds } from '@/lib/env';
import { getJson, ApiError } from '@/lib/api/client';

import {
  CatalogApiError,
  normalizeCatalogSearchFilters,
  serializeCatalogSearchFilters,
  type CatalogSearchFilters,
  type NormalizedCatalogSearchFilters
} from './catalog.api';

export type CatalogProviderMarker = {
  id: number;
  slug: string;
  name: string;
  provinceSlug: string | null;
  latitude: number;
  longitude: number;
};

const markersResponseSchema = z.object({
  items: z.array(
    z.object({
      id: z.number(),
      slug: z.string(),
      name: z.string(),
      provinceSlug: z.string().nullable(),
      latitude: z.number(),
      longitude: z.number()
    })
  )
});
type MarkersResponse = z.infer<typeof markersResponseSchema>;

export type CatalogProviderMarkersLoaderResult = {
  markers: CatalogProviderMarker[];
  filters: NormalizedCatalogSearchFilters;
  degraded: boolean;
};

/**
 * Loads filtered map markers for /puppies pages. Uses the lightweight
 * `/catalog/providers/markers` endpoint (no joins) so it stays fast even at
 * 1000+ pins. Filters are passed through so the map shows only providers
 * that match the current breed/province/location search.
 *
 * Pagination params on `filters` are intentionally ignored — the markers
 * endpoint is unbounded by design; the result list paginates separately.
 *
 * Degrades to an empty marker set on any upstream CatalogApiError so an
 * unhealthy catalog API never crashes the public Server Components tree
 * (matches the safety net in `loadCatalogProviders`).
 */
export const loadCatalogProviderMarkers = cache(
  async (
    filters?: CatalogSearchFilters
  ): Promise<CatalogProviderMarkersLoaderResult> => {
    const normalized = normalizeCatalogSearchFilters(filters);

    const params = serializeCatalogSearchFilters(normalized);
    // Strip pagination + include flags — the markers endpoint doesn't accept
    // them and we don't want to bust the upstream cache key over noise.
    params.delete('page');
    params.delete('limit');
    params.delete('includeListings');
    params.delete('includeWaitlists');

    try {
      const payload = await getJson<
        typeof markersResponseSchema,
        MarkersResponse
      >('/catalog/providers/markers', {
        query: Object.fromEntries(params.entries()),
        schema: markersResponseSchema,
        next: {
          revalidate: apiRevalidateSeconds,
          tags: ['catalog:providers']
        }
      });

      return {
        markers: payload.items,
        filters: normalized,
        degraded: false
      };
    } catch (error) {
      const wrapped =
        error instanceof ApiError
          ? new CatalogApiError(
              `Catalog markers request failed (${error.status ?? 'unknown'})`,
              { status: error.status, cause: error.body ?? error.cause }
            )
          : error;

      if (wrapped instanceof CatalogApiError) {
        // eslint-disable-next-line no-console
        console.error(
          '[loadCatalogProviderMarkers] degrading to empty markers',
          {
            status: wrapped.status,
            message: wrapped.message
          }
        );
        return { markers: [], filters: normalized, degraded: true };
      }
      throw wrapped;
    }
  }
);
